var _excluded = ["allowFullscreen", "landscapeAdaptiveSource", "refs", "type"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useEffect, useState, forwardRef } from 'react';
import { PropTypes } from 'prop-types';
import { getDeviceBreakpoint, getDeviceOrientation } from './breakpoints';
import Scene7VideoSourceProvider from './scene7-video-source-provider';
import AdaptiveVideoSourceProvider from './adaptive-video-source-provider';
var VIDEO_TYPE_LAYOUTS = {
  autoplay: 'minimal',
  minimal: 'minimal',
  inline: 'inline',
  stacked: 'stacked',
  series: 'stacked'
};
var VideoPlayer = /*#__PURE__*/forwardRef((_ref, ref) => {
  var {
      allowFullscreen,
      landscapeAdaptiveSource,
      refs: {
        nextInSeriesButton: nextInSeriesButtonRef,
        previousInSeriesButton: previousInSeriesButtonRef,
        startButton: startButtonRef,
        video: videoRef
      },
      type
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  var [device, setDevice] = useState('desktop');
  var [orientation, setOrientation] = useState('landscape');
  useEffect(() => {
    setDevice(getDeviceBreakpoint());
    setOrientation(getDeviceOrientation());
  }, []);
  var videoProps = _objectSpread({
    // If allowFullscreen is unset (null/undefined)
    // default to `true` unless it's an autoplay video
    allowFullscreen: allowFullscreen !== null && allowFullscreen !== void 0 ? allowFullscreen : type !== 'autoplay',
    inline: type === 'inline',
    autoplay: type === 'autoplay',
    controlLayout: VIDEO_TYPE_LAYOUTS[type],
    device,
    isPartOfSeries: type === 'series',
    loop: type === 'autoplay',
    nextInSeriesButtonRef,
    orientation,
    previousInSeriesButtonRef,
    showDuration: type !== 'autoplay',
    startButtonRef,
    videoRef
  }, rest);
  return landscapeAdaptiveSource ? /*#__PURE__*/React.createElement(AdaptiveVideoSourceProvider, _extends({
    ref: ref,
    landscapeAdaptiveSource: landscapeAdaptiveSource
  }, videoProps)) : /*#__PURE__*/React.createElement(Scene7VideoSourceProvider, _extends({
    ref: ref
  }, videoProps));
});
VideoPlayer.displayName = 'VideoPlayer';
VideoPlayer.propTypes = {
  /** Whether to show Unmute button and volume in controls */
  allowAudio: PropTypes.bool,
  /** Whether to show Fullscreen button in controls */
  allowFullscreen: PropTypes.bool,
  /** Text to be shown in the `title` attribute of the video */
  analyticsTitle: PropTypes.string,
  captionProps: PropTypes.shape({
    /** Label for the captions <track /> element (e.g., "English") */
    label: PropTypes.string,
    /** URL of the .vtt file for captions */
    src: PropTypes.string.isRequired,
    /** Must be a valid BCP 47 primary language subtag (https://www.w3.org/International/questions/qa-choosing-language-tags) */
    srcLang: PropTypes.string
  }),
  /** Class to be attached to the outer wrapper, outside the intersection observer */
  className: PropTypes.string,
  /** (Deprecated; prefer adaptive video.) Combined with `presetPrefix` to identify the name of the Scene7 preset for the component */
  componentName: PropTypes.string,
  /** URL of the .m3u8 playlist file for default or landscape oriented video */
  landscapeAdaptiveSource: PropTypes.string,
  /** Provide an image to be used as the video poster - accepts same props as Image component. For autoplay video, only src is used. */
  landscapePosterImageProps: PropTypes.shape({
    alt: PropTypes.string,
    src: PropTypes.string.isRequired
  }),
  /** (Deprecated; prefer adaptive video.) The base required MP4 video source */
  landscapeSource: PropTypes.string,
  /** USE SPARINGLY! Causes video to launch in fullscreen on initial play. This is a VERY aggressive feature and should be used with extreme prejudice */
  launchFullscreenOnStart: PropTypes.bool,
  /** An optional class attached to the play/pause button */
  playPauseButtonClassName: PropTypes.string,
  /** An optional adaptive video source preferred when in portrait orientation */
  portraitAdaptiveSource: PropTypes.string,
  /** Provide a portrait-oriented image to be used as the video poster - accepts same props as Image component. For autoplay video, only src is used. */
  portraitPosterImageProps: PropTypes.shape({
    alt: PropTypes.string,
    src: PropTypes.string.isRequired
  }),
  /** (Deprecated; prefer adaptive video.) An optional video source preferred when in portrait orientation */
  portraitSource: PropTypes.string,
  /** (Deprecated; prefer adaptive video.) Combined with `componentName` to identify the name of the Scene7 preset for the component - only necessary when a single component handles multiple video sizes */
  presetPrefix: PropTypes.string,
  /** Set to false for large videos, pages with many videos, or slow network connections etc. as the preloading of video files can affect page performance */
  prefetch: PropTypes.bool,
  refs: PropTypes.shape({
    nextInSeriesButton: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    previousInSeriesButton: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    startButton: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    video: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
  }),
  /** Video layout options for controls */
  type: PropTypes.oneOf(Object.keys(VIDEO_TYPE_LAYOUTS)),
  /** Class to be attached to the video */
  videoClassName: PropTypes.string
};
VideoPlayer.defaultProps = {
  allowAudio: true,
  allowFullscreen: null,
  analyticsTitle: null,
  captionProps: null,
  className: null,
  videoClassName: null,
  componentName: null,
  landscapeAdaptiveSource: null,
  landscapePosterImageProps: null,
  landscapeSource: null,
  launchFullscreenOnStart: false,
  playPauseButtonClassName: null,
  portraitAdaptiveSource: null,
  portraitPosterImageProps: null,
  portraitSource: null,
  prefetch: true,
  presetPrefix: '',
  refs: {
    nextInSeriesButton: null,
    previousInSeriesButton: null,
    startButton: null,
    video: null
  },
  type: 'inline'
};
export default VideoPlayer;